import React, { useState } from 'react';
import { AlertTriangle, ArrowRight, Building2, Calculator, Check, X, Percent, Scale, Edit3 } from 'lucide-react';

/**
 * Modal proposant la bascule vers une analyse SIRET par SIRET
 * quand l'analyse SIREN retourne NON_ELIGIBLE
 */
const ModalBasculeSIRET = ({
  isOpen,
  onClose,
  onConfirm,
  eligibiliteData,
  onCalculerParSiret,
  resultatsParSiret,
  isCalculating
}) => {
  const [modeRepartition, setModeRepartition] = useState('prorata');
  const [vacManuelles, setVacManuelles] = useState({});
  const [etape, setEtape] = useState(1); // 1: Proposition, 2: Choix répartition, 3: Résultats

  if (!isOpen || !eligibiliteData) return null;

  const { siret_groups, valeur_ajoutee_siren, ratio_siren, nb_siret } = eligibiliteData;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  // Calculer la VAC par SIRET selon le mode
  const getVacPreview = (siret, index) => {
    if (modeRepartition === 'manuel') {
      return vacManuelles[siret] || 0;
    } else if (modeRepartition === 'equitable') {
      return valeur_ajoutee_siren / nb_siret;
    } else if (modeRepartition === 'prorata') {
      const site = siret_groups.find(s => s.siret === siret);
      return site ? (site.pourcentage_consommation / 100) * valeur_ajoutee_siren : 0;
    }
    return 0;
  };

  // Validation mode manuel
  const isManuelValid = () => {
    if (modeRepartition !== 'manuel') return true;
    const totalManuel = Object.values(vacManuelles).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
    return Math.abs(totalManuel - valeur_ajoutee_siren) < 1; // Tolérance 1€
  };

  const handleCalculer = () => {
    const sitesWithVac = siret_groups.map(site => ({
      ...site,
      vac_calculee: getVacPreview(site.siret)
    }));
    
    onCalculerParSiret(modeRepartition, vacManuelles, sitesWithVac);
    setEtape(3);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="modal-bascule-siret">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6" />
              <h2 className="text-xl font-semibold" data-testid="modal-title">Entreprise non éligible au niveau SIREN</h2>
            </div>
            <button onClick={onClose} className="hover:bg-white/20 p-1 rounded" data-testid="modal-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Étape 1: Proposition de bascule */}
          {etape === 1 && (
            <div className="space-y-6">
              {/* Résultat SIREN */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                  <X className="w-5 h-5" />
                  Résultat analyse SIREN
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Ratio TICFE/VA :</span>
                    <span className="ml-2 font-mono font-medium">{formatPercent(ratio_siren)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Seuil requis :</span>
                    <span className="ml-2 font-mono font-medium">≥ 0,50%</span>
                  </div>
                </div>
              </div>

              {/* Proposition */}
              <div className="text-center py-4">
                <p className="text-lg text-gray-700 mb-2">
                  Votre entreprise dispose de <strong>{nb_siret} établissements (SIRET)</strong>.
                </p>
                <p className="text-gray-600">
                  Souhaitez-vous analyser l'éligibilité <strong>site par site</strong> ?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Certains sites pourraient être éligibles individuellement.
                </p>
              </div>

              {/* Liste des SIRET */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Établissements détectés
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {siret_groups.map((site, idx) => (
                    <div key={site.siret} className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm">
                      <div>
                        <span className="font-mono text-gray-600">{site.siret}</span>
                        {site.nom_site && <span className="ml-2 text-gray-500">({site.nom_site})</span>}
                      </div>
                      <div className="text-right text-gray-500">
                        {site.nb_pdls} PDL · {site.pourcentage_consommation}% conso
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  data-testid="btn-conserver-siren"
                >
                  Conserver résultat SIREN
                </button>
                <button
                  onClick={() => setEtape(2)}
                  className="px-4 py-2 bg-[#397DFF] text-white rounded-lg hover:bg-[#4672B6] flex items-center gap-2"
                  data-testid="btn-analyser-siret"
                >
                  Analyser par SIRET
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Étape 2: Choix de répartition VAC */}
          {etape === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  Répartition de la Valeur Ajoutée Comptable
                </h3>
                <p className="text-sm text-gray-600">
                  VAC totale (SIREN) : <strong>{formatCurrency(valeur_ajoutee_siren)}</strong>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Choisissez comment répartir cette VAC sur les {nb_siret} établissements.
                </p>
              </div>

              {/* Options de répartition */}
              <div className="space-y-3">
                {/* Option Prorata */}
                <label
                  className={`block border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    modeRepartition === 'prorata' ? 'border-[#397DFF] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="modeRepartition"
                      value="prorata"
                      checked={modeRepartition === 'prorata'}
                      onChange={(e) => setModeRepartition(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-gray-800">
                        <Percent className="w-4 h-4 text-[#397DFF]" />
                        Prorata de la consommation
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        VAC répartie proportionnellement à la consommation kWh de chaque SIRET
                      </p>
                    </div>
                  </div>
                </label>

                {/* Option Équitable */}
                <label
                  className={`block border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    modeRepartition === 'equitable' ? 'border-[#397DFF] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="modeRepartition"
                      value="equitable"
                      checked={modeRepartition === 'equitable'}
                      onChange={(e) => setModeRepartition(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-gray-800">
                        <Scale className="w-4 h-4 text-[#397DFF]" />
                        Répartition équitable
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        VAC divisée également entre les {nb_siret} établissements ({formatCurrency(valeur_ajoutee_siren / nb_siret)} chacun)
                      </p>
                    </div>
                  </div>
                </label>

                {/* Option Manuelle */}
                <label
                  className={`block border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    modeRepartition === 'manuel' ? 'border-[#397DFF] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="modeRepartition"
                      value="manuel"
                      checked={modeRepartition === 'manuel'}
                      onChange={(e) => setModeRepartition(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-gray-800">
                        <Edit3 className="w-4 h-4 text-[#397DFF]" />
                        Saisie manuelle
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Entrez la VAC pour chaque établissement (si données comptables disponibles)
                      </p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Aperçu répartition */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Aperçu de la répartition
                </h4>
                <div className="space-y-2">
                  {siret_groups.map((site) => (
                    <div key={site.siret} className="flex items-center justify-between bg-white rounded px-3 py-2">
                      <div className="flex-1">
                        <span className="font-mono text-sm text-gray-600">{site.siret}</span>
                        {site.nom_site && <span className="ml-2 text-xs text-gray-400">({site.nom_site})</span>}
                      </div>
                      {modeRepartition === 'manuel' ? (
                        <input
                          type="number"
                          value={vacManuelles[site.siret] || ''}
                          onChange={(e) => setVacManuelles({...vacManuelles, [site.siret]: e.target.value})}
                          placeholder="VAC en €"
                          className="w-32 px-2 py-1 border rounded text-right text-sm"
                        />
                      ) : (
                        <span className="font-medium text-[#397DFF]">
                          {formatCurrency(getVacPreview(site.siret))}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                
                {modeRepartition === 'manuel' && (
                  <div className={`mt-3 text-sm ${isManuelValid() ? 'text-green-600' : 'text-red-600'}`}>
                    Total saisi : {formatCurrency(Object.values(vacManuelles).reduce((s, v) => s + (parseFloat(v) || 0), 0))}
                    {!isManuelValid() && ` (doit être égal à ${formatCurrency(valeur_ajoutee_siren)})`}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <button
                  onClick={() => setEtape(1)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  data-testid="btn-retour-etape1"
                >
                  ← Retour
                </button>
                <button
                  onClick={handleCalculer}
                  disabled={!isManuelValid() || isCalculating}
                  className="px-4 py-2 bg-[#397DFF] text-white rounded-lg hover:bg-[#4672B6] flex items-center gap-2 disabled:opacity-50"
                  data-testid="btn-calculer-eligibilite"
                >
                  {isCalculating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Calcul en cours...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4" />
                      Calculer l'éligibilité
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Étape 3: Résultats par SIRET */}
          {etape === 3 && resultatsParSiret && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  Résultats de l'analyse par SIRET
                </h3>
                <p className="text-sm text-gray-600">
                  Mode de répartition : <strong>{modeRepartition === 'prorata' ? 'Prorata consommation' : modeRepartition === 'equitable' ? 'Équitable' : 'Manuelle'}</strong>
                </p>
              </div>

              {/* Résumé */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-800">{resultatsParSiret.nb_siret_total}</div>
                  <div className="text-sm text-gray-500">Établissements</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{resultatsParSiret.nb_siret_eligible}</div>
                  <div className="text-sm text-green-600">Éligibles</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{resultatsParSiret.nb_siret_non_eligible}</div>
                  <div className="text-sm text-red-600">Non éligibles</div>
                </div>
              </div>

              {/* Détail par SIRET */}
              <div className="space-y-2">
                {resultatsParSiret.resultats_siret.map((siret) => (
                  <div
                    key={siret.siret}
                    className={`flex items-center justify-between rounded-lg px-4 py-3 ${
                      siret.eligibilite === 'ELIGIBLE' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {siret.eligibilite === 'ELIGIBLE' ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <X className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <div className="font-mono text-sm">{siret.siret}</div>
                        {siret.nom_site && <div className="text-xs text-gray-500">{siret.nom_site}</div>}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="font-medium">VAC : {formatCurrency(siret.vac_siret)}</div>
                      <div className="text-gray-500">Ratio : {formatPercent(siret.ratio_siret)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <button
                  onClick={() => setEtape(2)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  data-testid="btn-modifier-repartition"
                >
                  ← Modifier répartition
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => onConfirm('consolide', resultatsParSiret)}
                    className="px-4 py-2 border border-[#397DFF] text-[#397DFF] rounded-lg hover:bg-blue-50"
                    data-testid="btn-rapport-consolide"
                  >
                    Rapport consolidé
                  </button>
                  <button
                    onClick={() => onConfirm('individuel', resultatsParSiret)}
                    className="px-4 py-2 bg-[#397DFF] text-white rounded-lg hover:bg-[#4672B6]"
                    data-testid="btn-rapports-individuels"
                  >
                    Rapports individuels
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalBasculeSIRET;
